import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { appendAppointmentsList } from '../../../store/memberSlice';
import TableHeader from '../../BaseComponents/TableHeader';
import AppointmentsTableRow from './AppointmentsTableRow';
import Loader from '../../BaseComponents/Loader';
import { getAppointmentsList } from '../../../api/appointments';
import { getDoctorsList } from '../../../api/doctors';
import { getNutritionistsList } from '../../../api/nutritionist';
import { AppointmentFilter, AppointmentLoadStrategy } from '../../../types/apiGeneratedTypes';
import './Appointments.scss';
import './AppointmentsTable.scss';

const headerColumns = [
  { title: 'Appointment status', key: 'status', minWidth: '176px', flex: 0 },
  { title: 'Appointment type', key: 'type', minWidth: '176px', flex: 0 },
  { title: 'Date', key: 'date', minWidth: '104px', flex: 0 },
  { title: 'Time', key: 'time', minWidth: '104px', flex: 0 },
  {
    title: 'Appointment name',
    key: 'testName',
    minWidth: '170px',
    flex: 0
  },
  { title: 'Assignee', key: 'assignee', minWidth: '160px', flex: 0 },
  { title: 'Location', key: 'location', minWidth: '180px', flex: 1 },
  { title: 'Meeting Link', key: 'meetingLink', minWidth: '90px', flex: 1 },
  { title: 'Appointment ID', key: 'appointmentId', minWidth: '170px', flex: 0 },
  {
    title: 'Biomarker Results',
    key: 'test-results',
    minWidth: '180px',
    flex: 0
  },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 }
];

interface IProps {
  currentSelectedFilter: 'all' | 'upcoming' | 'past' | 'future-tests' | 'pmp-alerts';
  isLoading: boolean;
  onInuviBookingClick: (appointmentId: number) => Promise<void>;
  setIsAppointmentView: (isViewOnly: boolean) => void;
}

const AppointmentsList: React.FC<IProps> = ({
  currentSelectedFilter,
  isLoading,
  onInuviBookingClick,
  setIsAppointmentView
}) => {
  const dispatch = useDispatch();
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [doctorNameById, setDoctorNameById] = React.useState<Record<string, string>>(
    {}
  );
  const [nutritionistNameById, setNutritionistNameById] = React.useState<
    Record<string, string>
  >({});
  const { memberData, appointmentsList, limit } = useSelector(
    (state: RootState) => state.member
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const offset = useRef(0);
  const scrollTimeoutRef = useRef<number | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    offset.current = 0;
    setHasMore(true);
    isFetchingRef.current = false;
  }, [currentSelectedFilter, memberData?.user_id]);

  useEffect(() => {
    let cancelled = false;
    const fetchAssignees = async () => {
      try {
        const [doctorsRes, nutritionistsRes] = await Promise.all([
          getDoctorsList(0, 1000),
          getNutritionistsList(0, 1000),
        ]);

        if (cancelled) return;

        const doctorsMap: Record<string, string> = {};
        (doctorsRes.doctors || []).forEach((d: any) => {
          const id = d?.user_id ?? d?.id;
          const name = `${d?.first_name || ''} ${d?.last_name || ''}`.trim();
          if (id && name) doctorsMap[String(id)] = name;
        });
        setDoctorNameById(doctorsMap);

        const nutritionistsMap: Record<string, string> = {};
        (nutritionistsRes.nutritionists || []).forEach((n: any) => {
          const id = n?.user_id ?? n?.id;
          const name = `${n?.first_name || ''} ${n?.last_name || ''}`.trim();
          if (id && name) nutritionistsMap[String(id)] = name;
        });
        setNutritionistNameById(nutritionistsMap);
      } catch {
        // Non-fatal: table can still render without names
      }
    };
    fetchAssignees();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMoreAppointments = useCallback(async () => {
    if (!memberData?.user_id || isFetchingRef.current || !hasMore) return;

    isFetchingRef.current = true;
    setLoadingMore(true);
    const nextOffset = offset.current + limit;
    try {
      const appointmentsListResp = await getAppointmentsList(
        limit,
        nextOffset,
        memberData.user_id,
        AppointmentLoadStrategy.ALL_DATA,
        currentSelectedFilter === 'upcoming'
          ? AppointmentFilter.Upcoming
          : currentSelectedFilter === 'past'
            ? AppointmentFilter.Past
            : undefined
      );

      offset.current = nextOffset;
      if (appointmentsListResp.appointments.length > 0) {
        dispatch(appendAppointmentsList(appointmentsListResp.appointments));
        setHasMore(appointmentsListResp.appointments.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('fetchAppointments error :>> ', error);
    } finally {
      isFetchingRef.current = false;
      setLoadingMore(false);
    }
  }, [currentSelectedFilter, dispatch, limit, memberData?.user_id, hasMore]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !hasMore || isFetchingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

    if (isNearBottom || isContentSmallerThanContainer) {
      loadMoreAppointments();
    }
  }, [hasMore, loadMoreAppointments]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const onScroll = () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = window.setTimeout(() => {
        handleScroll();
      }, 100);
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });

    if (hasMore && appointmentsList.length > 0 && !isLoading) {
      setTimeout(() => handleScroll(), 100);
    }

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll, hasMore, appointmentsList.length, isLoading]);

  return (
    <div className='scroll-container members-appointments-table'>
      <div className='table-container'>
        <TableHeader headerColumns={headerColumns} />
        <div className='member-table-body' ref={scrollContainerRef}>
          {isLoading && <Loader />}
          {appointmentsList &&
            !isLoading &&
            appointmentsList.map((appointment) => (
              <AppointmentsTableRow
                key={appointment.id}
                appointmentData={appointment}
                isUpcomingTab={currentSelectedFilter === 'upcoming'}
                currentSelectedFilter={currentSelectedFilter}
                onInuviBookingClick={onInuviBookingClick}
                doctorNameById={doctorNameById}
                nutritionistNameById={nutritionistNameById}
                setIsAppointmentView={setIsAppointmentView}
              />
            ))}
          {loadingMore && appointmentsList.length > 0 && (
            <div className='no-data' style={{ padding: '12px' }}>
              Loading more appointments...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentsList;
